/**
 * SoleadLab 2.0 — Finding Store
 *
 * B3.3: Zustand store for finding entity CRUD with status workflow.
 *
 * Findings are mutable — updates use optimistic concurrency (If-Match).
 * Status transitions are backend-enforced; the store surfaces transition
 * errors to the caller without swallowing them.
 *
 * Site-scoped: call setSiteId to switch sites, which clears stale items.
 *
 * State:
 *   items     — cached findings for the current site
 *   loading   — fetch in progress
 *   error     — last error message (null if none)
 *   siteId    — current site scope
 *
 * Actions:
 *   setSiteId(id)             — switch site scope (clears + refetches)
 *   fetchItems(filters?)      — load findings for current site
 *   getById(id)               — lookup a cached item by ID
 *   create(finding)           — create a new finding
 *   update(id, updates, ver)  — update with optimistic concurrency
 *   transition(id, status, ver) — convenience: update status only
 *   clear()                   — reset store state
 */

import { create } from 'zustand';
import {
  fetchFindings,
  createFinding,
  updateFinding,
  isConflictError,
  isStatusTransitionError,
  type Finding,
  type FindingStatus,
  type FindingFilters,
} from '../services/findingApi';

interface FindingState {
  items: Finding[];
  loading: boolean;
  error: string | null;
  siteId: string | null;

  setSiteId: (siteId: string) => void;
  fetchItems: (filters?: FindingFilters) => Promise<void>;
  getById: (id: string) => Finding | undefined;
  create: (
    finding: Parameters<typeof createFinding>[1],
  ) => Promise<Finding>;
  update: (
    id: string,
    updates: Parameters<typeof updateFinding>[2],
    version: number,
  ) => Promise<Finding>;
  transition: (id: string, status: FindingStatus, version: number) => Promise<Finding>;
  clear: () => void;
}

export const useFindingStore = create<FindingState>((set, get) => ({
  items: [],
  loading: false,
  error: null,
  siteId: null,

  setSiteId: (siteId: string) => {
    const current = get().siteId;
    if (current === siteId) return;
    set({ siteId, items: [], error: null });
  },

  fetchItems: async (filters?: FindingFilters) => {
    const { siteId } = get();
    if (!siteId) return;

    set({ loading: true, error: null });
    try {
      const result = await fetchFindings(siteId, filters);
      set({ items: result.items, loading: false });
    } catch (err) {
      set({
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch findings',
      });
    }
  },

  getById: (id: string) => {
    return get().items.find((item) => item.id === id);
  },

  create: async (finding) => {
    const { siteId } = get();
    if (!siteId) throw new Error('No site selected');

    const created = await createFinding(siteId, finding);
    set({ items: [...get().items, created] });
    return created;
  },

  update: async (id, updates, version) => {
    const { siteId } = get();
    if (!siteId) throw new Error('No site selected');

    try {
      const updated = await updateFinding(siteId, id, updates, version);
      set({
        items: get().items.map((item) =>
          item.id === id ? updated : item,
        ),
      });
      return updated;
    } catch (err) {
      if (isConflictError(err)) {
        set({ error: 'Version conflict — another update occurred. Please refresh.' });
      } else if (isStatusTransitionError(err)) {
        set({ error: err instanceof Error ? err.message : 'Invalid status transition.' });
      }
      throw err;
    }
  },

  transition: async (id, status, version) => {
    return get().update(id, { status }, version);
  },

  clear: () => {
    set({ items: [], siteId: null, error: null, loading: false });
  },
}));
